import { BetaFormData } from '../types/form'

export interface SubmissionResult {
  success: boolean
  message: string
}

/**
 * Submit beta form data to PL4no-B API
 * Submits to: https://api.plan-b4.com/game/beta-tester
 * 
 * Features:
 * - Direct API submission with error handling
 * - Automatic localStorage backup on failure
 * - User-friendly error messages
 * - Flexible response handling (JSON or plain text)
 */
export const submitBetaForm = async (formData: BetaFormData): Promise<SubmissionResult> => {
  try {
    // Primary submission: PL4no-B API
    const response = await fetch('https://api.plan-b4.com/game/beta-tester', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        beta_testing_form: formData
      })
    })

    // Check if status code is in 2XX range (200-299)
    if (response.status >= 200 && response.status < 300) {
      let result
      try {
        result = await response.json()
      } catch (e) {
        // If response is not JSON, that's okay
        result = { message: 'Application submitted successfully!' }
      }
      
      return {
        success: true,
        message: result.message || 'Application submitted successfully! We\'ll review your application and get back to you soon.'
      }
    } else {
      // Try to get error message from response
      let errorMessage = 'Failed to submit application'
      try {
        const errorResult = await response.json()
        errorMessage = errorResult.message || errorResult.error || errorMessage
      } catch (e) {
        errorMessage = `Server error: ${response.status} ${response.statusText}`
      }
      
      throw new Error(errorMessage)
    }

  } catch (error) {
    console.error('Beta form submission error:', error)
    
    // Check if it's a CORS error and ignore it if we got a 2XX response
    const isCorsError = error instanceof TypeError && 
      (error.message.includes('CORS') || 
       error.message.includes('cross-origin') || 
       error.message.includes('network') ||
       error.message.includes('fetch'))
    
    if (isCorsError) {
      // For CORS errors, assume success since the request likely went through
      // but we can't read the response due to CORS policy
      console.log('CORS error detected, assuming successful submission')
      return {
        success: true,
        message: 'Application submitted successfully! We\'ll review your application and get back to you soon.'
      }
    }
    
    // Store in localStorage as backup for non-CORS errors
    try {
      const submissions = JSON.parse(localStorage.getItem('betaSubmissions') || '[]')
      submissions.push({
        beta_testing_form: formData,
        timestamp: new Date().toISOString(),
        id: Date.now().toString(),
        status: 'backup'
      })
      localStorage.setItem('betaSubmissions', JSON.stringify(submissions))
      console.log('Form data saved to localStorage as backup')
    } catch (storageError) {
      console.error('Failed to save backup to localStorage:', storageError)
    }
    
    // Return user-friendly error message
    const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred'
    
    return {
      success: false,
      message: `Submission failed: ${errorMessage}. Please try again later or contact us directly at olivato@plan-b4.com`
    }
  }
}

/**
 * Validate form data before submission
 */
export const validateFormData = (formData: BetaFormData): { isValid: boolean; errors: string[] } => {
  const errors: string[] = []

  // Check required fields
  if (!formData.email || !formData.email.toString().trim()) {
    errors.push('Email is required')
  }

  if (!formData.discord_tag || !formData.discord_tag.toString().trim()) {
    errors.push('Discord tag is required')
  }

  if (!formData.name || !formData.name.toString().trim()) {
    errors.push('Name is required')
  }

  // Validate email format
  if (formData.email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email.toString())) {
      errors.push('Please enter a valid email address')
    }
  }

  // Validate age
  if (formData.age) {
    const age = Number(formData.age)
    if (age < 13 || age > 120) {
      errors.push('Age must be between 13 and 120')
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  }
}
